import {
  FireScenario,
} from '@fire-calc/domain-ts';
import { JsonScenarioSerializer } from '../services/JsonScenarioSerializer';

const STORAGE_KEY = 'fire_calc:scenarios';

export interface PersistedState {
  scenarios: FireScenario[];
  selectedId: string | null;
}

export class ScenarioRepository {
  private readonly storage: Storage;
  private readonly serializer: JsonScenarioSerializer;
  private writeTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(storage: Storage, serializer: JsonScenarioSerializer = new JsonScenarioSerializer()) {
    this.storage = storage;
    this.serializer = serializer;
  }

  load(): PersistedState {
    const raw = this.storage.getItem(STORAGE_KEY);
    if (!raw) return { scenarios: [], selectedId: null };
    try {
      const parsed = JSON.parse(raw) as PersistedState;
      if (!Array.isArray(parsed.scenarios)) {
        return { scenarios: [], selectedId: null };
      }
      return {
        scenarios: parsed.scenarios,
        selectedId: parsed.selectedId ?? null,
      };
    } catch {
      return { scenarios: [], selectedId: null };
    }
  }

  list(): FireScenario[] {
    return this.load().scenarios;
  }

  get(id: string): FireScenario | null {
    return this.list().find((s) => s.id === id) ?? null;
  }

  upsert(scenario: FireScenario): void {
    const current = this.load();
    const next = scenario.copyWith({ updatedAt: new Date() });
    const idx = current.scenarios.findIndex((s) => s.id === next.id);
    if (idx >= 0) current.scenarios[idx] = next;
    else current.scenarios.push(next);
    this.scheduleWrite({
      ...current,
      selectedId: current.selectedId ?? next.id,
    });
  }

  remove(id: string): void {
    const current = this.load();
    const next = current.scenarios.filter((s) => s.id !== id);
    this.scheduleWrite({
      scenarios: next,
      selectedId: current.selectedId === id ? null : current.selectedId,
    });
  }

  select(id: string | null): void {
    const current = this.load();
    if (this.writeTimer) clearTimeout(this.writeTimer);
    this.writeNow({ ...current, selectedId: id });
  }

  toJson(scenario: FireScenario): string {
    return JSON.stringify(this.serializer.toJson(scenario), null, 2);
  }

  fromJson(payload: string): FireScenario {
    return this.serializer.fromJson(JSON.parse(payload));
  }

  private scheduleWrite(state: PersistedState): void {
    if (this.writeTimer) clearTimeout(this.writeTimer);
    this.writeTimer = setTimeout(() => {
      this.writeNow(state);
      this.writeTimer = null;
    }, 250);
  }

  private writeNow(state: PersistedState): void {
    const serialised = JSON.stringify(state);
    this.storage.setItem(STORAGE_KEY, serialised);
  }
}

export const SCENARIO_STORAGE_KEY = STORAGE_KEY;
